import { getEnv, types } from "mobx-state-tree";
import { PricesStorageModel } from "./PricesStorage";
import { api } from "../api/ApiService";
import { activityPricesStorage, ActivityTypes, getActivityLabel } from "./ActivityPricesStorage";
import { ColumnType } from "../../components/datatable/interfaces";
import { GridColDef } from "@mui/x-data-grid";
import CounterCell from "./CounterCell";
import React from "react";
import { translationService } from "../translation/TranslationService";
import { reaction } from "mobx";
import { organizationSubscriptionService } from "../organization/OrganizationSubscription";
import { Flags } from "../../generated/FeatureFlags";
import { featureFlagsStorage } from "../feature-flags/FeatureFlagsStorage";

export const getOrderColumns = (translationService: any): GridColDef[] => [
  {
    field: "service",
    headerName: translationService.translate("shop.activity-prices.app"),
    type: ColumnType.String,
    width: 120,
    sortable: false,
    renderCell: (params) => <div className="wrap-cell">{params.value}</div>,
  },
  {
    field: "orderedApps",
    headerName: translationService.translate("shop.activity-prices.ordered"),
    type: ColumnType.Number,
    width: 140,
    sortable: false,
    renderCell: (params) => <CounterCell value={params.row.orderedApps} activity={params.row.id} />,
  },
  {
    field: "totalOrderedCredits",
    headerName: translationService.translate("shop.activity-prices.total-credits"),
    type: ColumnType.Number,
    flex: 1,
    sortable: false,
    align: "center",
  },
  {
    field: "totalCostEuro",
    headerName: translationService.translate("shop.activity-prices.total-euro"),
    type: ColumnType.Number,
    flex: 1,
    sortable: false,
    align: "center",
    renderCell: (params) => <div style={{fontWeight: params.row.bold ? "bold" : "normal"}}>{params.row.totalCostEuro}</div>
  },
];

const OrderStorageModel = types
  .model({
    conversionAmount: types.optional(types.number, 0),
    analysisAmount: types.optional(types.number, 0),
    highlightAmount: types.optional(types.number, 0),
    ocrAmount: types.optional(types.number, 0),
    analysisAndSegmentationAmount: types.optional(types.number, 0)
  })
  .named("OrderStorageModel")
  .actions((self) => ({
    setConversionAmount(amount: number) {
      self.conversionAmount = amount;
    },
    setAnalysisAmount(amount: number) {
      self.analysisAmount = amount;
    },
    setHighlightAmount(amount: number) {
      self.highlightAmount = amount;
    },
    setOcrAmount(amount: number) {
      self.ocrAmount = amount;
    },
    setAnalysisAndSegmentationAmount(amount: number) {
      self.analysisAndSegmentationAmount = amount;
    },
    reset() {
      self.conversionAmount = 0;
      self.analysisAmount = 0;
      self.highlightAmount = 0;
      self.ocrAmount = 0;
      self.analysisAndSegmentationAmount = 0;
    },
  }))
  .views((self) => ({
    get columns() {
      const translationService = getEnv(self).translationService;
      return getOrderColumns(translationService);
    },
    get rows() {
      const translationService = getEnv(self).translationService;
      const featureFlagsStorage = getEnv(self).featureFlagsStorage;
      
      const ocrItem =  {
        id: ActivityTypes.VideoOcr,
        service: translationService.translate(getActivityLabel([ActivityTypes.VideoOcr])),
        orderedApps: self.ocrAmount,
        totalOrderedCredits: this.ocrTotalCredits,
        totalCostEuro: this.ocrTotalEuros + " €",
      }
      const items = 
      [
        {
          id: -3,
          service: translationService.translate("shop.activity-prices.final-row"),
          orderedApps: -1,
          totalOrderedCredits: "",
          totalCostEuro: this.finalEuros + " €",
          bold: true
        },
        {
          id: -2,
          service: translationService.translate("shop.activity-prices.discount-row"),
          orderedApps: -1,
          totalOrderedCredits: `${this.discount}%`,
          totalCostEuro: this.discountEuros + " €",
        },
        {
          id: -1,
          service: translationService.translate("shop.activity-prices.total-row"),
          orderedApps: -1,
          totalOrderedCredits: this.totalCredits,
          totalCostEuro: this.totalEuros + " €",
        },
        {
          id: ActivityTypes.VideoConversion,
          service: translationService.translate(getActivityLabel([ActivityTypes.VideoConversion])),
          orderedApps: self.conversionAmount,
          totalOrderedCredits: this.conversionTotalCredits,
          totalCostEuro: this.conversionTotalEuros + " €",
        },
        {
          id: ActivityTypes.HighlightDetection,
          service: translationService.translate(getActivityLabel([ActivityTypes.HighlightDetection])),
          orderedApps: self.highlightAmount,
          totalOrderedCredits: this.highlightTotalCredits,
          totalCostEuro: this.highlightTotalEuros + " €",
        },
        {
          id: ActivityTypes.VideoAnalysis,
          service: translationService.translate(getActivityLabel([ActivityTypes.VideoAnalysis])),
          orderedApps: self.analysisAmount,
          totalOrderedCredits: this.analysisTotalCredits,
          totalCostEuro: this.analysisTotalEuros + " €",
        },
        {
          id: ActivityTypes.VideoAnalysisAndSegmentation,
          service: translationService.translate(getActivityLabel([ActivityTypes.VideoAnalysis, ActivityTypes.HighlightDetection])),
          orderedApps: self.analysisAndSegmentationAmount,
          totalOrderedCredits: this.analysisAndSegmentationTotalCredits,
          totalCostEuro: this.analysisAndSegmentationTotalEuros + " €",
        }
      ]
      if (featureFlagsStorage.isFeatureEnabled(Flags.Enable_Ocr)) items.push(ocrItem)

      return items.reverse();
    },
    get conversionTotalCredits() {
      return (
        self.conversionAmount *
        getEnv(self).activityPricesStorage.getPrice(ActivityTypes.VideoConversion)
      );
    },
    get conversionTotalEuros() {
      return this.conversionTotalCredits * getEnv(self).pricesStorage.minimumRatio;
    },
    get analysisTotalCredits() {
      return (
        self.analysisAmount *
        getEnv(self).activityPricesStorage.getPrice(ActivityTypes.VideoAnalysis)
      );
    },
    get analysisTotalEuros() {
      return this.analysisTotalCredits * getEnv(self).pricesStorage.minimumRatio;
    },
    get highlightTotalCredits() {
      return (
        self.highlightAmount *
        getEnv(self).activityPricesStorage.getPrice(ActivityTypes.HighlightDetection)
      );
    },
    get highlightTotalEuros() {
      return this.highlightTotalCredits * getEnv(self).pricesStorage.minimumRatio;
    },
    get ocrTotalCredits() {
      return self.ocrAmount * getEnv(self).activityPricesStorage.getPrice(ActivityTypes.VideoOcr);
    },
    get ocrTotalEuros() {
      return this.ocrTotalCredits * getEnv(self).pricesStorage.minimumRatio;
    },
    get analysisAndSegmentationTotalCredits() {
      return self.analysisAndSegmentationAmount * getEnv(self).activityPricesStorage.getPrice(ActivityTypes.VideoAnalysisAndSegmentation);
    },
    get analysisAndSegmentationTotalEuros() {
      return this.analysisAndSegmentationTotalCredits * getEnv(self).pricesStorage.minimumRatio;
    },
    get totalCredits() {
      return this.conversionTotalCredits + this.analysisTotalCredits + this.highlightTotalCredits + this.ocrTotalCredits + this.analysisAndSegmentationTotalCredits;
    },
    get totalEuros() {
      return this.conversionTotalEuros + this.analysisTotalEuros + this.highlightTotalEuros + this.ocrTotalEuros + this.analysisAndSegmentationTotalEuros;
    },
    get discount() {
      return getEnv(self).pricesStorage.getDiscount(this.totalCredits);
    },
    get discountEuros() {
      return (this.totalEuros * this.discount) / 100;
    },
    get finalEuros() {
      return this.totalEuros - this.discountEuros;
    },
  }));

const pricesStorage = PricesStorageModel.create({ noPagination: true }, { api });

export const orderStorage = OrderStorageModel.create(
  { conversionAmount: 0, analysisAmount: 0, highlightAmount: 0 },
  {
    activityPricesStorage: activityPricesStorage,
    pricesStorage: pricesStorage,
    translationService,
    featureFlagsStorage
  }
);

reaction( 
  () => organizationSubscriptionService.key,
  async () => {
    await pricesStorage.load();
})
